import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import { movies, ratingRanges } from "@/mocks/movies";
import FilterSidebar, { type Filters } from "./components/FilterSidebar";

const PAGE_SIZE = 8;

const sortOptions = [
  { key: "latest", label: "最新" },
  { key: "rating", label: "评分" },
  { key: "hot", label: "热度" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const filters: Filters = {
    genre: searchParams.get("genre") ?? "",
    region: searchParams.get("region") ?? "",
    year: searchParams.get("year") ?? "",
    rating: searchParams.get("rating") ?? "",
  };
  const sort = searchParams.get("sort") ?? "latest";
  const currentPage = Math.max(1, Number(searchParams.get("page") ?? 1));

  const filtered = useMemo(() => {
    const range = ratingRanges.find((r) => r.label === filters.rating);
    const list = movies.filter((m) => {
      if (filters.genre && !m.genres.includes(filters.genre)) return false;
      if (filters.region && m.region !== filters.region) return false;
      if (filters.year && `${m.year}` !== filters.year) return false;
      if (range && (m.rating < range.min || m.rating >= range.max)) return false;
      return true;
    });
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === "hot") sorted.sort((a, b) => b.hot - a.hot);
    else sorted.sort((a, b) => b.year - a.year || b.hot - a.hot);
    return sorted;
  }, [filters.genre, filters.region, filters.year, filters.rating, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const page = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeLabels = [
    filters.genre,
    filters.region,
    filters.year,
    filters.rating,
  ].filter(Boolean);

  usePageMeta({
    title:
      activeLabels.length > 0
        ? `${activeLabels.join(" · ")} 电影分类 - 555电影`
        : "电影分类 - 555电影",
    description:
      activeLabels.length > 0
        ? `555电影分类浏览：当前筛选 ${activeLabels.join("、")}，共 ${filtered.length} 部影片，支持按类型、地区、年份、评分组合筛选。`
        : "555电影分类页，支持按类型、地区、年份、评分区间组合筛选，快速找到你想看的电影。",
    keywords: "电影分类,电影筛选,按类型看电影,555电影片库",
  });

  const updateParams = (next: Record<string, string | null>, resetPage = true) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === "") params.delete(k);
      else params.set(k, v);
    });
    if (resetPage) params.delete("page");
    setSearchParams(params);
  };

  const onSelect = (field: keyof Filters, value: string) => updateParams({ [field]: value });
  const onReset = () => setSearchParams(new URLSearchParams());

  const hasActive = activeLabels.length > 0;

  const sortBar = (
    <div className="flex items-center gap-2">
      <span className="hidden text-sm text-foreground-500 sm:block">排序</span>
      <div className="flex rounded-full border border-background-200 bg-background-100 p-1">
        {sortOptions.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => updateParams({ sort: opt.key })}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              sort === opt.key
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:text-foreground-950"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full animate-fadeIn">
      {/* Page header */}
      <div className="w-full bg-primary-950">
        <div className="mx-auto w-full max-w-screen-xl px-4 py-8 md:px-6 md:py-12">
          <div className="mb-3 text-background-300">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影分类" }]} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
            电影分类
          </h1>
          <p className="mt-2 text-sm text-background-300">
            多条件组合筛选，从 {movies.length} 部精选影片中寻找你的下一部电影
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-screen-xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          {/* Sidebar */}
          <aside className="w-full lg:w-64 lg:shrink-0">
            <button
              type="button"
              onClick={() => setMobileFilterOpen((v) => !v)}
              className="mb-3 flex w-full items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-semibold text-foreground-900 lg:hidden"
            >
              <span className="flex items-center gap-2">
                <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                  <i className="ri-filter-3-line text-sm"></i>
                </span>
                筛选条件{hasActive ? `（${activeLabels.length}）` : ""}
              </span>
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
              </span>
            </button>
            <div className={`${mobileFilterOpen ? "block" : "hidden"} lg:block lg:sticky lg:top-24`}>
              <FilterSidebar
                filters={filters}
                onSelect={onSelect}
                onReset={onReset}
                hasActive={hasActive}
              />
            </div>
          </aside>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部影片
              </p>
              {sortBar}
            </div>

            {hasActive && (
              <div className="mb-5 flex flex-wrap items-center gap-2">
                {activeLabels.map((label) => (
                  <span
                    key={label}
                    className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700"
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}

            {pageItems.length === 0 ? (
              <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </span>
                <p className="text-base font-medium text-foreground-800">未找到相关电影</p>
                <p className="mt-1 text-sm text-foreground-500">试试调整或重置筛选条件</p>
                <button
                  type="button"
                  onClick={onReset}
                  className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                  {pageItems.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination
                  current={page}
                  totalPages={totalPages}
                  onChange={(p) => updateParams({ page: `${p}` }, false)}
                />
              </>
            )}

            <div className="mt-8 rounded-lg bg-secondary-50 p-4 text-xs text-secondary-800">
              <span className="mr-1 inline-flex items-center gap-1 font-semibold">
                <i className="ri-information-line"></i>
                说明：
              </span>
              当前筛选、排序与分页均为前端演示效果，数据为示范内容，后续可接入真实接口。
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </span>
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}